import { $el, $list, transitionToPromise } from 'src/scripts/utils/utils'
import { BaseComponent } from 'src/scripts/components/base-component'
import { isMobile } from 'src/scripts/utils/check-media'
import { debounce } from 'src/scripts/utils/debounce'

export class FeaturedNavigation extends BaseComponent {
  elementsTemplateAttribute: string
  lineAttribute: string
  imageAttribute: string
  buttonAttribute: string
  imageActiveClass: string
  buttonActiveClass: string
  buttonIconAttribute: string

  template: HTMLTemplateElement
  floatingImg: HTMLImageElement
  floatingBtn: HTMLImageElement
  container: Element
  lines: NodeListOf<Element>
  images: HTMLImageElement[]
  buttons: HTMLAnchorElement[]
  iconPlaceholder: Node
  scrollingType: string
  scrollingTypeAttribute: string

  imageWidth: number
  imageHeight: number
  buttonWidth: number
  buttonHeight: number

  isAnimating: boolean
  forceStopped: boolean
  mouseIsOn: boolean
  isMobile: boolean

  resizeObserver: ResizeObserver

  activeImage: HTMLImageElement
  activeButton: HTMLAnchorElement

  animation: number

  x: number
  y: number
  mouseY: number

  constructor() {
    super()

    this.elementsTemplateAttribute = '[data-elements-template]'
    this.lineAttribute = '[data-navigation-line]'
    this.imageAttribute = '[data-navigation-line-image]'
    this.buttonAttribute = '[data-navigation-line-button]'
    this.imageActiveClass = 'featured-navigation__link-image--active'
    this.buttonActiveClass = 'featured-navigation__btn--active'
    this.buttonIconAttribute = '[data-button-without-text]'
    this.scrollingTypeAttribute = 'data-scrolling-type'

    this.template = this.querySelector(this.elementsTemplateAttribute)
    this.floatingImg = this.template.content.querySelector(this.imageAttribute)
    this.floatingBtn = this.template.content.querySelector(this.buttonAttribute)
    this.container = this.querySelector('.container')
    this.lines = this.querySelectorAll(this.lineAttribute)
    this.images = $list(this.imageAttribute, this)
    this.buttons = $list(this.buttonAttribute, this)
    this.iconPlaceholder = $el(
      this.buttonIconAttribute,
      this,
    )?.firstElementChild?.cloneNode(true)
    this.scrollingType = this.getAttribute(this.scrollingTypeAttribute)

    this.isAnimating = false
    this.forceStopped = true

    this.isMobile = isMobile()

    this.resizeObserver = new ResizeObserver(this.handleResize)
    this.resizeObserver.observe(this)
  }

  connectedCallback(): void {
    if (!this.isMobile) {
      this.initDesktop()
    }
  }

  disconnectedCallback(): void {
    this.deInitDesktop()
  }

  initDesktop = (): void => {
    this.initFloatingElements()

    this.setRects(this.floatingImg, this.buttons[0])

    this.addEventListener('mouseenter', this.handleMouseEnter)

    this.lines.forEach(line => {
      if (this.scrollingType === 'none') {
        line
          .querySelector('.featured-navigation__link-text')
          .addEventListener('mouseenter', this.handleMouseEnterLine)

        line
          .querySelector('.featured-navigation__link-text')
          .addEventListener('mouseleave', this.handleMouseLeaveLine)
      } else {
        line.addEventListener('mouseenter', this.handleMouseEnterLine)
      }
    })
  }

  deInitDesktop = (): void => {
    this.deinitFloatingElements()

    this.removeEventListener('mouseenter', this.handleMouseEnter)
    this.lines.forEach(line => {
      if (this.scrollingType === 'none') {
        line
          .querySelector('.featured-navigation__link-text')
          .removeEventListener('mouseenter', this.handleMouseEnterLine)

        line
          .querySelector('.featured-navigation__link-text')
          .removeEventListener('mouseleave', this.handleMouseLeaveLine)
      } else {
        line.removeEventListener('mouseenter', this.handleMouseEnterLine)
      }
    })

    document.removeEventListener('mousemove', this.handleMouseMove)
  }

  initFloatingElements = (): void => {
    this.activeImage = this.floatingImg.cloneNode(true) as HTMLImageElement
    this.activeButton = this.floatingBtn.cloneNode(true) as HTMLAnchorElement

    this.container.appendChild(this.activeImage)
    this.container.appendChild(this.activeButton)

    this.scaleDown()
  }

  deinitFloatingElements = (): void => {
    this.activeImage.remove()
    this.activeButton.remove()
  }

  handleMouseEnterLine = (event: PointerEvent): void => {
    const { targetImage, targetButton } = this.getTargetImageAndButton(event)

    this.replaceFloatingElementsData(targetImage, targetButton)

    this.forceStopped = false

    if (!this.isAnimating) {
      this.handleMouseEnter(event)
    }
  }

  handleMouseLeaveLine = () => {
    this.scaleDown()
  }

  replaceFloatingElementsData = (
    imageData: HTMLImageElement,
    buttonData: HTMLAnchorElement,
  ): void => {
    if (imageData) {
      this.activeImage.src = imageData?.src
      this.activeImage.srcset = imageData?.srcset
      this.activeImage.style.maxHeight = `${
        imageData.getBoundingClientRect().height
      }px`

      this.toggleImageActiveClass(this.activeImage, 'add')
      this.activeImage.style.opacity = '1'
    } else {
      this.toggleImageActiveClass(this.activeImage, 'add')
      this.activeImage.style.opacity = '0'
    }

    if (buttonData) {
      if (buttonData.hasAttribute('data-button-without-text')) {
        this.activeButton.replaceChildren(this.iconPlaceholder)
      } else {
        this.activeButton.textContent = buttonData.textContent
      }

      this.activeButton.className = `${buttonData.className} ${this.buttonActiveClass}`
      this.activeButton.href = buttonData.href

      this.toggleButtonActiveClass(this.activeButton, 'add')
      this.activeButton.style.opacity = '1'
    } else {
      this.activeButton.style.opacity = '0'
    }

    this.scaleUp()
    this.setRects(imageData, buttonData)
  }

  handleMouseEnter = (event: PointerEvent): void => {
    this.activeImage.style.translate = `${event.clientX - this.imageWidth}px ${
      event.clientY - this.imageHeight
    }px`

    this.activeButton.style.translate = `${
      event.clientX - this.buttonWidth
    }px ${event.clientY - this.buttonHeight}px`

    if (!this.mouseIsOn) {
      document.addEventListener('mousemove', this.handleMouseMove)
      this.toggleScrollListener(true)
    }

    this.mouseIsOn = true
  }

  handleMouseLeave = (): void => {
    this.scaleDown()
    this.toggleScrollListener(false)
  }

  toggleScrollListener = (add: boolean): void => {
    if (add) {
      this.addListener(window, 'scroll', this.handleScroll)
    } else {
      this.removeListener(window, 'scroll', this.handleScroll)
    }
  }

  handleScroll = debounce(() => {
    const { top, bottom } = this.getBoundingClientRect()

    if (this.mouseY < top || this.mouseY > bottom) {
      this.handleMouseLeave()
    }
  }, 100)

  handleMouseMove = (event: PointerEvent): void => {
    if (!this.forceStopped) {
      const target = event.target as HTMLElement

      if (target === this.container || !target.closest('featured-navigation')) {
        this.handleMouseLeave()
      }

      const { x, y, mouseY } = this.getMouseCoordinates(
        event.clientX,
        event.clientY,
      )

      this.x = x
      this.y = y
      this.mouseY = mouseY

      if (!this.isAnimating) {
        this.startAnimation()
      }
    }
  }

  startAnimation = (): void => {
    this.isAnimating = true
    this.animation = requestAnimationFrame(this.tick)
  }

  stopAnimation = (): void => {
    this.isAnimating = false
    cancelAnimationFrame(this.animation)

    const cancelAnimation =
      this.activeImage.style.scale === '0' &&
      this.activeButton.style.scale === '0'

    if (cancelAnimation) {
      this.forceStopped = true

      this.toggleImageActiveClass(this.activeImage, 'remove')
      this.toggleButtonActiveClass(this.activeButton, 'remove')

      document.removeEventListener('mousemove', this.handleMouseMove)
      this.mouseIsOn = false
    }
  }

  tick = (time: number): void => {
    const { x, y, width } = this.activeImage
      ? this.activeImage.getBoundingClientRect()
      : this.activeButton.getBoundingClientRect()

    const targetXimage = +(this.x - x).toFixed(3)
    const targetYimage = +(this.y - y).toFixed(3)
    const targetXbutton = targetXimage + this.imageWidth - this.buttonWidth
    const targetYbutton = targetYimage + this.imageHeight - this.buttonHeight

    this.draw(
      targetXimage + x,
      targetYimage + y,
      targetXbutton + x,
      targetYbutton + y,
    )

    if (targetXimage !== 0 && targetYimage !== 0 && width > 0) {
      this.animation = requestAnimationFrame(this.tick)
    } else {
      this.stopAnimation()
    }
  }

  draw = (
    imageX: number,
    imageY: number,
    buttonX: number,
    buttonY: number,
  ): void => {
    this.activeImage.style.translate = `${imageX}px ${imageY}px`
    this.activeButton.style.translate = `${buttonX}px ${buttonY}px`
  }

  getTargetImageAndButton = (
    event,
  ): {
    targetImage: HTMLImageElement | undefined
    targetButton: HTMLAnchorElement | undefined
  } => {
    const targetLine = event.currentTarget
    const id = targetLine.dataset.navigationLine || targetLine.dataset.lineId

    const targetImage = this.images.find(
      img => img.dataset.navigationLineImage === id,
    )
    const targetButton = this.buttons.find(
      btn => btn.dataset.navigationLineButton === id,
    )

    return { targetImage, targetButton }
  }

  setRects = (
    targetImage: HTMLImageElement,
    targetButton: HTMLAnchorElement,
  ): void => {
    this.imageWidth = this.activeImage.offsetWidth / 2
    this.imageHeight = targetImage
      ? targetImage.scrollHeight / 2
      : this.activeImage.offsetHeight / 2

    this.buttonWidth = targetButton?.offsetWidth / 2
    this.buttonHeight = targetButton?.offsetHeight / 2
  }

  getMouseCoordinates = (
    x: number,
    y: number,
  ): { x: number; y: number; mouseY: number } => {
    return {
      x: x - this.imageWidth,
      y: y - this.imageHeight,
      mouseY: y,
    }
  }

  toggleButtonActiveClass = (element: HTMLElement, action: string): void => {
    element?.classList.toggle(this.buttonActiveClass, action === 'add')
  }

  toggleImageActiveClass = (element: HTMLElement, action: string): void => {
    element?.classList.toggle(this.imageActiveClass, action === 'add')
  }

  scaleUp = (): void => {
    this.activeImage.style.scale = '1'
    this.activeButton.style.scale = '1'
  }

  scaleDown = (): void => {
    this.activeImage.style.scale = '0'
    this.activeButton.style.scale = '0'
  }

  handleResize = debounce(() => {
    if (isMobile() === this.isMobile) {
      return
    }

    if (isMobile()) {
      this.deInitDesktop()
    } else {
      this.initDesktop()
    }

    this.isMobile = isMobile()
  }, 200)
}
